import { channelSelector } from '../channel-selector';
class MobileNavigationHandler {
    constructor(config = {}) {
        var _a, _b, _c, _d, _e;
        this.navElement = null;
        this.hamburgerButton = null;
        this.mainPanel = null;
        this.subPanel = null;
        this.liveRegion = null;
        this.isOpen = false;
        this.currentView = 'main';
        this.currentParent = '';
        this.focusableElements = [];
        this.lastFocusedElement = null;
        this.boundHandlers = {
            keydown: this.handleKeydown.bind(this),
            popstate: this.handlePopState.bind(this),
            hamburgerClick: this.handleHamburgerClick.bind(this),
            closeClick: this.handleCloseClick.bind(this),
            backClick: this.handleBackClick.bind(this),
            submenuTriggerClick: this.handleSubmenuTriggerClick.bind(this)
        };
        this.config = {
            debug: (_a = config.debug) !== null && _a !== void 0 ? _a : false,
            animationDuration: (_b = config.animationDuration) !== null && _b !== void 0 ? _b : 0,
            hiddenClass: (_c = config.hiddenClass) !== null && _c !== void 0 ? _c : 'u-hidden',
            returnFocusOnClose: (_d = config.returnFocusOnClose) !== null && _d !== void 0 ? _d : true,
            useHistoryAPI: (_e = config.useHistoryAPI) !== null && _e !== void 0 ? _e : true
        };
        this.log('MobileNavigationHandler initialized with config:', this.config);
    }
    init() {
        this.log('Initializing mobile navigation...');
        this.navElement = document.getElementById('mobile-nav');
        this.hamburgerButton = document.querySelector('[data-mobile-nav-hamburger]');
        this.mainPanel = document.querySelector('[data-panel="main"]');
        this.subPanel = document.querySelector('[data-panel="sub"]');
        this.liveRegion = document.getElementById('mobile-nav-status');
        if (!this.navElement) {
            this.log('Mobile navigation element not found - skipping initialization');
            return;
        }
        if (!this.hamburgerButton) {
            console.warn('Mobile navigation hamburger button not found');
            return;
        }
        if (!this.mainPanel || !this.subPanel) {
            console.warn('Mobile navigation panels not found');
            return;
        }
        this.log('All required elements found');
        this.attachEventListeners();
        this.updateFocusableElements();
        this.initializeChannelSelector();
        this.log('Mobile navigation initialized successfully');
    }
    attachEventListeners() {
        if (this.hamburgerButton) {
            this.hamburgerButton.addEventListener('click', this.boundHandlers.hamburgerClick);
        }
        if (this.navElement) {
            this.navElement.addEventListener('click', (e) => {
                const target = e.target;
                const closeButton = target.closest('[data-mobile-nav-close]');
                if (closeButton) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.log('Close button clicked via delegation');
                    this.close();
                    return;
                }
                const backButton = target.closest('[data-mobile-nav-back]');
                if (backButton) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.log('Back button clicked via delegation');
                    this.navigateToMain();
                    return;
                }
                const trigger = target.closest('[data-mobile-nav-trigger]');
                if (trigger) {
                    this.boundHandlers.submenuTriggerClick(e);
                    return;
                }
            });
        }
        document.addEventListener('keydown', this.boundHandlers.keydown);
        if (this.config.useHistoryAPI) {
            window.addEventListener('popstate', this.boundHandlers.popstate);
        }
        this.log('Event listeners attached');
    }
    handleHamburgerClick(e) {
        e.preventDefault();
        this.log('Hamburger clicked');
        this.open();
    }
    handleCloseClick(e) {
        e.preventDefault();
        this.log('Close button clicked');
        this.close();
    }
    handleBackClick(e) {
        e.preventDefault();
        this.log('Back button clicked');
        this.navigateToMain();
    }
    handleSubmenuTriggerClick(e) {
        e.preventDefault();
        const trigger = e.target.closest('[data-mobile-nav-trigger]');
        if (!trigger)
            return;
        const parentName = trigger.getAttribute('data-parent-name');
        if (!parentName) {
            console.warn('Submenu trigger missing data-parent-name attribute');
            return;
        }
        this.log('Submenu trigger clicked:', parentName);
        this.navigateToSub(parentName);
    }
    handleKeydown(e) {
        if (!this.isOpen)
            return;
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                this.handleEscapeKey();
                break;
            case 'Tab':
                this.handleTabBoundary(e);
                break;
        }
    }
    handleEscapeKey() {
        this.log('Escape key pressed');
        if (this.currentView === 'sub') {
            this.navigateToMain();
        }
        else {
            this.close();
        }
    }
    handleTabBoundary(e) {
        if (!this.navElement)
            return;
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length === 0)
            return;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        if (e.shiftKey && activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
            this.log('Focus wrapped to last element');
        }
        else if (!e.shiftKey && activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
            this.log('Focus wrapped to first element');
        }
    }
    getFocusableElements() {
        const activePanel = this.currentView === 'sub' ? this.subPanel : this.mainPanel;
        if (!activePanel) {
            this.log('No active panel found');
            return [];
        }
        const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const elements = Array.from(activePanel.querySelectorAll(selector));
        this.log(`Found ${elements.length} focusable elements in ${this.currentView} panel before filtering`);
        const visiblePanelElements = elements.filter((el) => {
            if (el.classList.contains(this.config.hiddenClass)) {
                return false;
            }
            const parentMenu = el.closest('[data-menu]');
            if (parentMenu && parentMenu.classList.contains(this.config.hiddenClass)) {
                return false;
            }
            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') {
                return false;
            }
            return true;
        });
        this.log(`${visiblePanelElements.length} visible elements after filtering`);
        return visiblePanelElements;
    }
    updateFocusableElements() {
        this.focusableElements = this.getFocusableElements();
        this.log(`Focusable elements updated: ${this.focusableElements.length} elements`);
        if (this.config.debug && this.focusableElements.length > 0) {
            this.log('First 5 focusable elements:', this.focusableElements.slice(0, 5).map(el => {
                var _a;
                return ({
                    tag: el.tagName,
                    text: (_a = el.textContent) === null || _a === void 0 ? void 0 : _a.trim().substring(0, 30),
                    data: el.getAttribute('data-mobile-nav-close') || el.getAttribute('data-mobile-nav-back') || ''
                });
            }));
        }
    }
    open() {
        if (this.isOpen) {
            this.log('Navigation already open');
            return;
        }
        this.log('Opening mobile navigation');
        this.lastFocusedElement = document.activeElement;
        if (this.navElement) {
            this.navElement.classList.remove(this.config.hiddenClass);
            this.navElement.setAttribute('aria-hidden', 'false');
        }
        document.body.style.overflow = 'hidden';
        this.isOpen = true;
        this.currentView = 'main';
        this.updateFocusableElements();
        if (this.config.useHistoryAPI) {
            const state = { mobileNav: true, view: 'main' };
            history.pushState(state, '', window.location.href);
            this.log('History state pushed:', state);
        }
        this.announce('Navigation menu opened');
        this.log('Menu opened, focus remains on hamburger button');
        this.dispatchEvent('mobile-nav:opened');
    }
    close(options = {}) {
        if (!this.isOpen) {
            this.log('Navigation already closed');
            return;
        }
        this.log('Closing mobile navigation', options);
        if (this.navElement) {
            this.navElement.classList.add(this.config.hiddenClass);
            this.navElement.setAttribute('aria-hidden', 'true');
        }
        document.body.style.overflow = '';
        this.resetToMainView();
        this.isOpen = false;
        if (!options.skipFocusReturn && this.config.returnFocusOnClose && this.lastFocusedElement) {
            setTimeout(() => {
                var _a;
                (_a = this.lastFocusedElement) === null || _a === void 0 ? void 0 : _a.focus();
                this.log('Focus returned to:', this.lastFocusedElement);
            }, 50);
        }
        if (!options.skipHistory && this.config.useHistoryAPI) {
            const currentState = history.state;
            if (currentState && currentState.mobileNav) {
                history.back();
                this.log('History back called');
            }
        }
        this.announce('Navigation menu closed');
        this.dispatchEvent('mobile-nav:closed');
    }
    navigateToSub(parentName) {
        var _a, _b;
        if (!this.isOpen)
            return;
        this.log('Navigating to sub menu:', parentName);
        const sanitizedParent = this.sanitizeInput(parentName);
        const submenu = (_a = this.subPanel) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-mobile-nav-submenu][data-parent="${sanitizedParent}"]`);
        if (!submenu) {
            console.warn(`Submenu not found for parent: ${sanitizedParent}`);
            return;
        }
        (_b = this.subPanel) === null || _b === void 0 ? void 0 : _b.querySelectorAll('[data-mobile-nav-submenu]').forEach(menu => {
            menu.classList.add(this.config.hiddenClass);
        });
        submenu.classList.remove(this.config.hiddenClass);
        if (this.mainPanel) {
            this.mainPanel.style.transform = 'translateX(-100%)';
        }
        if (this.subPanel) {
            this.subPanel.style.transform = 'translateX(0)';
        }
        this.currentView = 'sub';
        this.currentParent = sanitizedParent;
        if (this.navElement) {
            this.navElement.setAttribute('data-current-view', 'sub');
        }
        this.updateFocusableElements();
        setTimeout(() => {
            var _a;
            const backButton = (_a = this.subPanel) === null || _a === void 0 ? void 0 : _a.querySelector('[data-mobile-nav-back]');
            if (backButton) {
                backButton.focus();
                this.log('Focus moved to back button');
            }
        }, this.config.animationDuration + 50);
        if (this.config.useHistoryAPI) {
            const state = { mobileNav: true, view: 'sub', parent: sanitizedParent };
            history.pushState(state, '', window.location.href);
            this.log('History state pushed:', state);
        }
        this.announce(`Opened ${sanitizedParent} submenu`);
        this.dispatchEvent('mobile-nav:submenu-opened', { parent: sanitizedParent });
    }
    navigateToMain() {
        if (!this.isOpen || this.currentView !== 'sub')
            return;
        this.log('Navigating to main menu');
        if (this.mainPanel) {
            this.mainPanel.style.transform = 'translateX(0)';
        }
        if (this.subPanel) {
            this.subPanel.style.transform = 'translateX(100%)';
        }
        const previousParent = this.currentParent;
        this.currentView = 'main';
        this.currentParent = '';
        if (this.navElement) {
            this.navElement.setAttribute('data-current-view', 'main');
        }
        this.updateFocusableElements();
        setTimeout(() => {
            var _a;
            const closeButton = (_a = this.mainPanel) === null || _a === void 0 ? void 0 : _a.querySelector('[data-mobile-nav-close]');
            if (closeButton) {
                closeButton.focus();
                this.log('Focus moved to close button');
            }
        }, this.config.animationDuration + 50);
        if (this.config.useHistoryAPI) {
            history.back();
            this.log('History back called');
        }
        this.announce('Returned to main menu');
        this.dispatchEvent('mobile-nav:returned-to-main', { previousParent });
    }
    resetToMainView() {
        var _a;
        this.log('Resetting to main view');
        if (this.mainPanel) {
            this.mainPanel.style.transform = 'translateX(0)';
        }
        if (this.subPanel) {
            this.subPanel.style.transform = 'translateX(100%)';
        }
        (_a = this.subPanel) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[data-mobile-nav-submenu]').forEach(menu => {
            menu.classList.add(this.config.hiddenClass);
        });
        this.currentView = 'main';
        this.currentParent = '';
        if (this.navElement) {
            this.navElement.setAttribute('data-current-view', 'main');
        }
    }
    handlePopState(e) {
        const state = e.state;
        this.log('Popstate event:', state);
        if (!state || !state.mobileNav) {
            if (this.isOpen) {
                this.log('Closing menu due to history navigation');
                if (this.navElement) {
                    this.navElement.classList.add(this.config.hiddenClass);
                    this.navElement.setAttribute('aria-hidden', 'true');
                }
                document.body.style.overflow = '';
                this.resetToMainView();
                this.isOpen = false;
                if (this.config.returnFocusOnClose && this.lastFocusedElement) {
                    setTimeout(() => {
                        var _a;
                        (_a = this.lastFocusedElement) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 50);
                }
                this.announce('Navigation menu closed');
                this.dispatchEvent('mobile-nav:closed');
            }
            return;
        }
        if (state.view !== 'main' && state.view !== 'sub') {
            console.warn('Invalid view in history state:', state.view);
            return;
        }
        if (state.view === 'main' && this.currentView === 'sub') {
            this.log('History navigation: sub -> main');
            if (this.mainPanel) {
                this.mainPanel.style.transform = 'translateX(0)';
            }
            if (this.subPanel) {
                this.subPanel.style.transform = 'translateX(100%)';
            }
            this.currentView = 'main';
            this.currentParent = '';
            if (this.navElement) {
                this.navElement.setAttribute('data-current-view', 'main');
            }
            this.updateFocusableElements();
            setTimeout(() => {
                var _a;
                const closeButton = (_a = this.mainPanel) === null || _a === void 0 ? void 0 : _a.querySelector('[data-mobile-nav-close]');
                if (closeButton) {
                    closeButton.focus();
                }
            }, this.config.animationDuration + 50);
            this.announce('Returned to main menu');
            this.dispatchEvent('mobile-nav:returned-to-main');
        }
    }
    announce(message) {
        if (!this.liveRegion)
            return;
        this.liveRegion.textContent = message;
        this.log('Announced to screen readers:', message);
    }
    dispatchEvent(eventName, detail = {}) {
        const event = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: true,
            detail
        });
        if (this.navElement) {
            this.navElement.dispatchEvent(event);
            this.log('Event dispatched:', eventName, detail);
        }
    }
    sanitizeInput(input) {
        return input.replace(/[<>\"']/g, '').trim();
    }
    log(...args) {
        if (this.config.debug) {
            console.log('[MobileNav]', ...args);
        }
    }
    initializeChannelSelector() {
        try {
            this.log('Initializing channel selector for mobile navigation');
            channelSelector.init('[data-channel-selector-button]', '[data-channel-selector-text]', '[data-channel-selector-links]');
        }
        catch (error) {
            console.error('Error initializing mobile channel selector:', error);
        }
    }
    isNavigationOpen() {
        return this.isOpen;
    }
    getCurrentView() {
        return this.currentView;
    }
    refresh() {
        this.log('Refreshing mobile navigation');
        this.updateFocusableElements();
    }
    destroy() {
        this.log('Destroying mobile navigation');
        if (this.hamburgerButton) {
            this.hamburgerButton.removeEventListener('click', this.boundHandlers.hamburgerClick);
        }
        document.removeEventListener('keydown', this.boundHandlers.keydown);
        if (this.config.useHistoryAPI) {
            window.removeEventListener('popstate', this.boundHandlers.popstate);
        }
        if (this.isOpen) {
            this.close();
        }
        this.log('Mobile navigation destroyed');
    }
}
let mobileNavigationInstance = null;
export const mobileNavigation = {
    init(config) {
        if (!mobileNavigationInstance) {
            mobileNavigationInstance = new MobileNavigationHandler(config);
            mobileNavigationInstance.init();
        }
        return mobileNavigationInstance;
    },
    getInstance() {
        return mobileNavigationInstance;
    },
    destroy() {
        if (mobileNavigationInstance) {
            mobileNavigationInstance.destroy();
            mobileNavigationInstance = null;
        }
    }
};
if (typeof window !== 'undefined') {
    window.mobileNavigation = mobileNavigation;
}
